import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSubscription, SUBSCRIPTION_PLANS } from '../contexts/SubscriptionContext';
import DashboardLayout from '../components/DashboardLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiCrown, FiZap, FiActivity, FiEdit3, FiCheckCircle, FiX, FiPlus, FiTrash2 } = FiIcons;

const SubscriptionPage = () => {
  const { subscription, upgradePlan } = useSubscription();
  
  // Notification State
  const [notification, setNotification] = useState(null);

  // Utilities Management State
  const [utilities, setUtilities] = useState([
    {
      id: 'electricity',
      name: 'Electricity',
      icon: FiZap,
      periods: [
        { id: 'elec-1', startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '5.00', description: 'Standard Rate' }
      ],
      sameAllYear: true,
      isActive: true,
      isCustom: false
    },
    {
      id: 'gas',
      name: 'Gas',
      icon: FiActivity,
      periods: [
        { id: 'gas-1', startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '8.00', description: 'Standard Rate' }
      ],
      sameAllYear: true,
      isActive: true,
      isCustom: false
    },
    {
      id: 'cleaning',
      name: 'Cleaning',
      icon: FiActivity,
      periods: [
        { id: 'clean-1', startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '25.00', description: 'One-time fee' }
      ],
      sameAllYear: true,
      isActive: false,
      isCustom: false
    },
    {
      id: 'bedsheets',
      name: 'Bed Sheets',
      icon: FiActivity,
      periods: [
        { id: 'bed-1', startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '15.00', description: 'One-time fee' }
      ],
      sameAllYear: true,
      isActive: false,
      isCustom: false
    }
  ]);

  const [applyToAll, setApplyToAll] = useState(false);
  const [selectedUtilitiesForApply, setSelectedUtilitiesForApply] = useState([]);
  const [editingUtility, setEditingUtility] = useState(null);
  const [utilityChanges, setUtilityChanges] = useState({});
  const [showAddUtility, setShowAddUtility] = useState(false);
  
  const [newUtility, setNewUtility] = useState({
    name: '',
    icon: FiActivity,
    periods: [
      { id: 'new-1', startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '', description: '' }
    ],
    sameAllYear: true,
    isActive: true
  });

  const handleUpgrade = async (planId) => {
    if (planId === 'custom') {
      window.open('mailto:sales@venuemanager.com?subject=Custom Plan Inquiry');
      return;
    }
    await upgradePlan(planId);
  };

  const startEditingUtility = (utilityId) => {
    setEditingUtility(utilityId);
    const utility = utilities.find(u => u.id === utilityId);
    setUtilityChanges({
      periods: [...utility.periods],
      sameAllYear: utility.sameAllYear,
      isActive: utility.isActive
    });
  };

  const cancelEditingUtility = () => {
    setEditingUtility(null);
    setUtilityChanges({});
  };

  const saveUtilityChanges = (utilityId) => {
    setUtilities(prev => prev.map(utility => 
      utility.id === utilityId ? { ...utility, ...utilityChanges } : utility
    ));
    setEditingUtility(null);
    setUtilityChanges({});
  };

  const handleUtilityPeriodChange = (utilityId, periodId, field, value) => {
    if (editingUtility === utilityId) {
      setUtilityChanges(prev => ({
        ...prev,
        periods: prev.periods.map(period => 
          period.id === periodId ? { ...period, [field]: value } : period
        )
      }));
    } else {
      setUtilities(prev => prev.map(utility => {
        if (utility.id === utilityId) {
          const updatedPeriods = [...utility.periods];
          const periodIndex = updatedPeriods.findIndex(p => p.id === periodId);
          if (periodIndex !== -1) {
            updatedPeriods[periodIndex] = { ...updatedPeriods[periodIndex], [field]: value };
          }
          return { ...utility, periods: updatedPeriods };
        }
        return utility;
      }));
    }
  };

  const addUtilityPeriod = (utilityId) => {
    if (editingUtility === utilityId) {
      const newPeriod = { id: `${utilityId}-${Date.now()}`, startDate: '', endDate: '', costPerDay: '', description: '' };
      setUtilityChanges(prev => ({
        ...prev,
        periods: [...prev.periods, newPeriod]
      }));
    } else {
      setUtilities(prev => prev.map(utility => {
        if (utility.id === utilityId) {
          return {
            ...utility,
            periods: [...utility.periods, { id: `${utilityId}-${Date.now()}`, startDate: '', endDate: '', costPerDay: '', description: '' }]
          };
        }
        return utility;
      }));
    }
  };

  const removeUtilityPeriod = (utilityId, periodId) => {
    if (editingUtility === utilityId) {
      setUtilityChanges(prev => ({
        ...prev,
        periods: prev.periods.filter(period => period.id !== periodId)
      }));
    } else {
      setUtilities(prev => prev.map(utility => {
        if (utility.id === utilityId && utility.periods.length > 1) {
          return {
            ...utility,
            periods: utility.periods.filter(period => period.id !== periodId)
          };
        }
        return utility;
      }));
    }
  };

  const toggleUtilitySameAllYear = (utilityId) => {
    if (editingUtility === utilityId) {
      const newSameAllYear = !utilityChanges.sameAllYear;
      setUtilityChanges(prev => {
        if (newSameAllYear) {
          return { ...prev, sameAllYear: true, periods: [prev.periods[0]] };
        } else {
          return { ...prev, sameAllYear: false };
        }
      });
    } else {
      setUtilities(prev => prev.map(utility => {
        if (utility.id === utilityId) {
          const newSameAllYear = !utility.sameAllYear;
          if (newSameAllYear) {
            return { ...utility, sameAllYear: true, periods: [{ ...utility.periods[0], startDate: '2024-01-01', endDate: '2024-12-31' }] };
          } else {
            return { ...utility, sameAllYear: false };
          }
        }
        return utility;
      }));
    }
  };

  const toggleUtilityActive = (utilityId) => {
    setUtilities(prev => prev.map(utility => 
      utility.id === utilityId ? { ...utility, isActive: !utility.isActive } : utility
    ));
  };

  const deleteUtility = (utilityId) => {
    if (window.confirm('Are you sure you want to delete this utility?')) {
      setUtilities(prev => prev.filter(utility => utility.id !== utilityId));
    }
  };

  const addNewUtility = () => {
    if (!newUtility.name.trim()) {
      alert('Please enter a utility name');
      return;
    }
    const utilityId = `custom-${Date.now()}`;
    setUtilities(prev => [...prev, {
      ...newUtility,
      id: utilityId,
      isCustom: true,
      periods: newUtility.periods.map(period => ({ ...period, id: `${utilityId}-1` }))
    }]);
    setNewUtility({
      name: '',
      icon: FiActivity,
      periods: [
        { id: 'new-1', startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '', description: '' }
      ],
      sameAllYear: true,
      isActive: true
    });
    setShowAddUtility(false);

    // Show Success Notification
    setNotification('Service added successfully');
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  const handleNewUtilityPeriodChange = (index, field, value) => {
    const updatedPeriods = [...newUtility.periods];
    updatedPeriods[index] = { ...updatedPeriods[index], [field]: value };
    setNewUtility(prev => ({ ...prev, periods: updatedPeriods }));
  };

  const addNewUtilityPeriod = () => {
    setNewUtility(prev => ({
      ...prev,
      periods: [...prev.periods, { id: `new-${Date.now()}`, startDate: '', endDate: '', costPerDay: '', description: '' }]
    }));
  };

  const removeNewUtilityPeriod = (index) => {
    if (newUtility.periods.length > 1) {
      const updatedPeriods = newUtility.periods.filter((_, i) => i !== index);
      setNewUtility(prev => ({ ...prev, periods: updatedPeriods }));
    }
  };

  const applyPeriodsToSelectedUtilities = () => {
    if (!applyToAll || selectedUtilitiesForApply.length === 0) return;
    
    const sourceUtility = utilities.find(u => u.id === 'electricity');
    const periodsToApply = sourceUtility.periods;

    setUtilities(prev => prev.map(utility => {
      if (selectedUtilitiesForApply.includes(utility.id) && utility.id !== 'electricity') {
        return {
          ...utility,
          periods: periodsToApply.map(period => ({
            ...period,
            id: `${utility.id}-${Date.now()}`
          })),
          sameAllYear: sourceUtility.sameAllYear
        };
      }
      return utility;
    }));
    
    setApplyToAll(false);
    setSelectedUtilitiesForApply([]);
  };

  const plans = [
    {
      ...SUBSCRIPTION_PLANS.FREE,
      buttonText: 'Current Plan',
      buttonDisabled: subscription?.plan.id === 'free'
    },
    {
      ...SUBSCRIPTION_PLANS.STANDARD,
      buttonText: subscription?.plan.id === 'standard' ? 'Current Plan' : 'Upgrade to Standard',
      buttonDisabled: subscription?.plan.id === 'standard',
      popular: false
    },
    {
      ...SUBSCRIPTION_PLANS.PRO,
      buttonText: subscription?.plan.id === 'pro' ? 'Current Plan' : 'Upgrade to Pro',
      buttonDisabled: subscription?.plan.id === 'pro',
      popular: true
    },
    {
      ...SUBSCRIPTION_PLANS.CUSTOM,
      buttonText: 'Contact Sales',
      buttonDisabled: false
    }
  ];

  return (
    <DashboardLayout>
      {/* Success Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -50, x: '-50%' }}
            className="fixed top-8 left-1/2 transform -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 font-medium"
          >
            <div className="bg-white/20 p-1 rounded-full">
              <SafeIcon icon={FiCheckCircle} className="h-5 w-5" />
            </div>
            {notification}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Subscription & Utilities</h1>
            <p className="text-gray-600 mt-1">Manage your plan and utility pricing</p>
          </div>
        </div>

        {/* Current Plan Status */}
        {subscription && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <SafeIcon icon={FiCrown} className="mr-2 h-5 w-5 text-blue-600" />
                  Current Plan: {subscription.plan.name}
                </h3>
                <p className="text-gray-600 mt-1">
                  {subscription.status === 'trial' 
                    ? `Trial ends ${new Date(subscription.trialEndsAt).toLocaleDateString()}` 
                    : 'Active subscription'}
                </p>
              </div>
              {subscription.plan.price !== 'Contact us' && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    €{subscription.plan.price}
                  </div>
                  <div className="text-sm text-gray-600">per month</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="grid md:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-white rounded-lg shadow-sm p-6 flex flex-col ${
                  plan.popular ? 'ring-2 ring-blue-600 relative' : 'border border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Recommended
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">
                      {typeof plan.price === 'number' ? `€${plan.price}` : plan.price}
                    </span>
                    {typeof plan.price === 'number' && (
                      <span className="text-gray-600">/month</span>
                    )}
                  </div>
                  {plan.maxVenues && (
                    <p className="text-gray-600">
                      Up to {plan.maxVenues === Infinity ? 'unlimited' : plan.maxVenues} venue{plan.maxVenues !== 1 && plan.maxVenues !== Infinity ? 's' : ''}
                    </p>
                  )}
                </div>
                <ul className="space-y-3 mb-6 flex-1">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-xs">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={plan.buttonDisabled}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                    plan.buttonDisabled
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : plan.popular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
                >
                  {plan.buttonText}
                </button>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Utility Pricing Management */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Utility Pricing Management</h2>
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500">
                Configure utility costs that will be added to your venue pricing (in Euro)
              </div>
              <button
                onClick={() => setShowAddUtility(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors inline-flex items-center"
              >
                <SafeIcon icon={FiPlus} className="mr-2 h-4 w-4" />
                Add Utility
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {utilities.map((utility) => {
              const isEditing = editingUtility === utility.id;
              const currentData = isEditing ? utilityChanges : utility;
              
              return (
                <div key={utility.id} className={`border rounded-lg p-6 ${isEditing ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`${isEditing ? 'bg-blue-100' : 'bg-blue-100'} p-2 rounded-lg mr-3`}>
                        <SafeIcon icon={utility.icon} className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{utility.name}</h3>
                        {utility.isCustom && (
                          <span className="text-xs text-gray-500">Custom utility</span>
                        )}
                      </div>
                      {isEditing && (
                        <span className="ml-3 bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                          Editing
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentData.isActive}
                          onChange={() => toggleUtilityActive(utility.id)}
                          disabled={isEditing}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {currentData.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                      
                      {!isEditing ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEditingUtility(utility.id)}
                            className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center"
                          >
                            <SafeIcon icon={FiEdit3} className="mr-1 h-3 w-3" />
                            Edit
                          </button>
                          {utility.isCustom && (
                            <button
                              onClick={() => deleteUtility(utility.id)}
                              className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center"
                            >
                              <SafeIcon icon={FiTrash2} className="mr-1 h-3 w-3" />
                              Delete
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => cancelEditingUtility()}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => saveUtilityChanges(utility.id)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center"
                          >
                            <SafeIcon icon={FiCheckCircle} className="mr-1 h-3 w-3" />
                            Save
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id={`${utility.id}-same`}
                          name={`periods-${utility.id}`}
                          checked={currentData.sameAllYear}
                          onChange={() => toggleUtilitySameAllYear(utility.id)}
                          disabled={!isEditing}
                          className="border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={`${utility.id}-same`} className="ml-2 text-sm text-gray-700">
                          Same cost all year
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id={`${utility.id}-different`}
                          name={`periods-${utility.id}`}
                          checked={!currentData.sameAllYear}
                          onChange={() => toggleUtilitySameAllYear(utility.id)}
                          disabled={!isEditing}
                          className="border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={`${utility.id}-different`} className="ml-2 text-sm text-gray-700">
                          Different periods
                        </label>
                      </div>
                    </div>

                    {currentData.sameAllYear ? (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cost per day (€)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={currentData.periods[0]?.costPerDay || ''}
                          onChange={(e) => handleUtilityPeriodChange(utility.id, currentData.periods[0]?.id, 'costPerDay', e.target.value)}
                          disabled={!isEditing}
                          className={`w-32 px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 ${isEditing ? 'border-blue-300 bg-white' : 'border-gray-300 bg-gray-100'}`}
                          placeholder="5.00"
                        />
                        {isEditing && (
                          <p className="text-xs text-blue-600 mt-2">Enter the daily cost in euros</p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {currentData.periods.map((period) => (
                          <div key={period.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-medium text-gray-900">
                                Period {currentData.periods.findIndex(p => p.id === period.id) + 1}
                              </h4>
                              {currentData.periods.length > 1 && isEditing && (
                                <button
                                  type="button"
                                  onClick={() => removeUtilityPeriod(utility.id, period.id)}
                                  className="text-red-600 hover:text-red-700 p-1"
                                >
                                  <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-4 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Start Date
                                </label>
                                <input
                                  type="date"
                                  value={period.startDate}
                                  onChange={(e) => handleUtilityPeriodChange(utility.id, period.id, 'startDate', e.target.value)}
                                  disabled={!isEditing}
                                  className={`w-full px-2 py-1.5 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm ${isEditing ? 'border-blue-300 bg-white' : 'border-gray-300 bg-gray-100'}`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  End Date
                                </label>
                                <input
                                  type="date"
                                  value={period.endDate}
                                  onChange={(e) => handleUtilityPeriodChange(utility.id, period.id, 'endDate', e.target.value)}
                                  disabled={!isEditing}
                                  className={`w-full px-2 py-1.5 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm ${isEditing ? 'border-blue-300 bg-white' : 'border-gray-300 bg-gray-100'}`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Cost/day (€)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={period.costPerDay}
                                  onChange={(e) => handleUtilityPeriodChange(utility.id, period.id, 'costPerDay', e.target.value)}
                                  disabled={!isEditing}
                                  className={`w-full px-2 py-1.5 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm ${isEditing ? 'border-blue-300 bg-white' : 'border-gray-300 bg-gray-100'}`}
                                  placeholder="5.00"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Description
                                </label>
                                <input
                                  type="text"
                                  value={period.description || ''}
                                  onChange={(e) => handleUtilityPeriodChange(utility.id, period.id, 'description', e.target.value)}
                                  disabled={!isEditing}
                                  className={`w-full px-2 py-1.5 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm ${isEditing ? 'border-blue-300 bg-white' : 'border-gray-300 bg-gray-100'}`}
                                  placeholder="Summer rate"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => addUtilityPeriod(utility.id)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center"
                          >
                            <SafeIcon icon={FiPlus} className="mr-1 h-3 w-3" />
                            Add Period
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="border-t border-gray-200 pt-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">Apply Electricity Periods to Other Utilities</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="applyToAll"
                      checked={applyToAll}
                      onChange={(e) => setApplyToAll(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="applyToAll" className="ml-2 text-sm text-blue-800">
                      Apply electricity pricing periods to other utilities
                    </label>
                  </div>
                  
                  {applyToAll && (
                    <div className="ml-6 space-y-2">
                      <p className="text-xs text-blue-700">Select utilities to apply:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {utilities.filter(u => u.id !== 'electricity').map((utility) => (
                          <div key={utility.id} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`apply-${utility.id}`}
                              checked={selectedUtilitiesForApply.includes(utility.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUtilitiesForApply([...selectedUtilitiesForApply, utility.id]);
                                } else {
                                  setSelectedUtilitiesForApply(selectedUtilitiesForApply.filter(id => id !== utility.id));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <label htmlFor={`apply-${utility.id}`} className="ml-2 text-sm text-blue-800">
                              {utility.name}
                            </label>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={applyPeriodsToSelectedUtilities}
                        disabled={selectedUtilitiesForApply.length === 0}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                      >
                        Apply to Selected
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Save All Utility Pricing
              </button>
            </div>
          </div>
        </div>

        {/* Add New Utility Modal */}
        {showAddUtility && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Add New Utility</h2>
                <button 
                  onClick={() => setShowAddUtility(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <SafeIcon icon={FiX} className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Utility Name
                  </label>
                  <input
                    type="text"
                    value={newUtility.name}
                    onChange={(e) => setNewUtility(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. Water, Internet, Maintenance"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Pricing Periods</h3>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="new-utility-same"
                        name="new-utility-periods"
                        checked={newUtility.sameAllYear}
                        onChange={() => setNewUtility(prev => ({ ...prev, sameAllYear: true }))}
                        className="border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="new-utility-same" className="ml-2 text-sm text-gray-700">
                        Same cost all year
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="new-utility-different"
                        name="new-utility-periods"
                        checked={!newUtility.sameAllYear}
                        onChange={() => setNewUtility(prev => ({ ...prev, sameAllYear: false }))}
                        className="border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="new-utility-different" className="ml-2 text-sm text-gray-700">
                        Different periods
                      </label>
                    </div>
                  </div>

                  {newUtility.sameAllYear ? (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cost per day (€)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newUtility.periods[0]?.costPerDay || ''}
                        onChange={(e) => handleNewUtilityPeriodChange(0, 'costPerDay', e.target.value)}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="5.00"
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {newUtility.periods.map((period, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-900">
                              Period {index + 1}
                            </h4>
                            {newUtility.periods.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeNewUtilityPeriod(index)}
                                className="text-red-600 hover:text-red-700 p-1"
                              >
                                <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Start Date
                              </label>
                              <input
                                type="date"
                                value={period.startDate}
                                onChange={(e) => handleNewUtilityPeriodChange(index, 'startDate', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                End Date
                              </label>
                              <input
                                type="date"
                                value={period.endDate}
                                onChange={(e) => handleNewUtilityPeriodChange(index, 'endDate', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Cost/day (€)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={period.costPerDay}
                                onChange={(e) => handleNewUtilityPeriodChange(index, 'costPerDay', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                                placeholder="5.00"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Description
                              </label>
                              <input
                                type="text"
                                value={period.description || ''}
                                onChange={(e) => handleNewUtilityPeriodChange(index, 'description', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                                placeholder="Summer rate"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addNewUtilityPeriod}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center"
                      >
                        <SafeIcon icon={FiPlus} className="mr-1 h-3 w-3" />
                        Add Period
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-4 p-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddUtility(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={addNewUtility}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                >
                  Add Utility
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionPage;